import { useState } from "react";
import { queryOptions, useQuery } from "@tanstack/react-query";
import {
  fetchTodaysTasks,
  completeTask as completeTaskApi,
  dismissTask as dismissTaskApi,
  submitReviewDecision,
} from "@api";
import { toast } from "@components/toast/Toast";
import { taskTag, taskTagStyle, taskTagIcon, taskDescription } from "./taskDescription";

// Kept in sync with the card's exit transition duration in Tasks.jsx.
export const EXIT_ANIMATION_MS = 300;

const toTask = (t) => {
  const task = {
    id: t.id,
    title: t.title || "Untitled task",
    due: t.due ? new Date(t.due) : null,
    customerId: t.customerId ?? null,
    customerName: t.customerName ?? null,
    paymentRequestId: t.paymentRequestId ?? null,
    // The plan/promise action behind a missed-payment task — opens the plan-detail modal.
    actionId: t.actionId ?? null,
    // The parked human_approval step a decision-graph review resolves — present only on those reviews;
    // its presence is what routes the card's actions to the decision endpoint vs the linear flow.
    stepId: t.stepId ?? null,
    taskType: t.taskType ?? null,
    instalmentNumber: t.instalmentNumber ?? null,
    instalmentCount: t.instalmentCount ?? null,
    promisedDate: t.promisedDate ?? null,
    promisedAmount: t.promisedAmount ?? null,
    // What the pre-send checks saw on an IncomingPaymentReview task — folded into the description so
    // the card shows the payment it spotted before the user sends anyway or dismisses.
    detectedAmount: t.detectedAmount ?? null,
    detectedAt: t.detectedAt ?? null,
    detectedCount: t.detectedCount ?? null,
    detectedSource: t.detectedSource ?? null,
    // "promise" | "schedule" — a review card reads as the send it gates (schedule vs promise copy/colour).
    scheduleKind: t.scheduleKind ?? null,
    // Which section the task belongs to: outstanding | snoozed | completed. The BFF applies the
    // snooze wake-up rule (an expired snooze comes back as outstanding), so it's read, not derived.
    group: t.group || "outstanding",
    // Read-time importance from the BFF: { score, band: critical|high|normal, reasons: [...] }.
    priority: t.taskPriority ?? null,
    snoozedUntil: t.snoozedUntil ? new Date(t.snoozedUntil) : null,
    completedAt: t.completedAt ? new Date(t.completedAt) : null,
    completedByUserId: t.completedByUserId ?? null,
    // The card shows the amount next to the title and hides it when absent.
    amount: t.promisedAmount ?? null,
  };
  task.tag = taskTag(task);
  task.tagStyle = taskTagStyle(task);
  task.tagIcon = taskTagIcon(task);
  // Missed-payment tasks phrase their description from facts — the calendar item carries no real
  // description (its SafeSummary is just the title). Other tasks use whatever text they carry.
  task.description = task.taskType ? taskDescription(task) : t.description || "";
  return task;
};

/**
 * The shared today's-tasks query — the Tasks page reads the full data, the sidebar reads just the
 * outstanding count (via `select`), off the SAME cache. So one fetch serves both, and the
 * complete/dismiss refetch below keeps the sidebar badge in sync for free.
 */
export const todaysTasksQueryOptions = queryOptions({
  queryKey: ["todaysTasks"],
  queryFn: async ({ signal }) => {
    const response = await fetchTodaysTasks({ signal });
    return {
      tasks: (response?.tasks ?? []).map(toTask),
      outstandingCount: response?.outstandingCount ?? 0,
      snoozedCount: response?.snoozedCount ?? 0,
      completedTodayCount: response?.completedTodayCount ?? 0,
      outstandingAmount: response?.outstandingAmount ?? 0,
    };
  },
  staleTime: 60_000,
  retry: false,
});

/**
 * Today's tasks for the signed-in user. The BFF returns one pre-sorted, pre-grouped response —
 * outstanding by importance, then snoozed by wake time, then today's completed — plus the header
 * aggregates, so nothing is re-sorted or re-counted here.
 */
export const useTasks = () => {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery(todaysTasksQueryOptions);

  // Tasks hidden optimistically while a complete/dismiss round-trips. Unlike before, an actioned
  // task doesn't vanish from the feed — it comes back in another section (snoozed or completed) —
  // so the local hide is lifted once the refetch has the task in its new home.
  const [dismissed, setDismissed] = useState(() => new Set());
  // Tasks mid exit-animation — still in the list (so the card can play its collapse/fade) but on
  // their way out.
  const [exiting, setExiting] = useState(() => new Set());

  const tasks = (data?.tasks ?? []).filter((t) => !dismissed.has(t.id));
  const outstanding = tasks.filter((t) => t.group === "outstanding");

  const criticalCount = outstanding.filter((t) => t.priority?.band === "critical").length;

  const setHidden = (taskId, on) =>
    setDismissed((prev) => {
      const next = new Set(prev);
      on ? next.add(taskId) : next.delete(taskId);
      return next;
    });
  const setExit = (taskId, on) =>
    setExiting((prev) => {
      const next = new Set(prev);
      on ? next.add(taskId) : next.delete(taskId);
      return next;
    });

  // Play the card's exit animation, then remove it. The API call fires immediately (in parallel with
  // the animation); on failure the card is restored, on success it reappears in its new section.
  const removeWithExit = (task, apiCall, failMessage) => {
    setExit(task.id, true);
    const done = apiCall(task.id);
    setTimeout(async () => {
      setHidden(task.id, true);
      setExit(task.id, false);
      try {
        await done;
        await refetch();
      } catch {
        toast({ description: failMessage });
      } finally {
        setHidden(task.id, false);
      }
    }, EXIT_ANIMATION_MS);
  };

  // X = delete the suggestion: the BFF cancels the calendar item and it drops out of the feed.
  const dismiss = (task) =>
    removeWithExit(task, dismissTaskApi, "Couldn't remove the task. Please try again.");

  // Called after a reminder is sent — closes the task with the acting user.
  const complete = (task) =>
    removeWithExit(task, completeTaskApi, "The reminder was sent, but the task couldn't be closed.");

  // A plain to-do has no reminder to send — completing it just marks it done.
  const markDone = (task) =>
    removeWithExit(task, completeTaskApi, "Couldn't complete the task. Please try again.");

  // Resolve a decision-graph review (a parked human_approval step): "send_anyway" resumes the send,
  // "dismiss" continues the plan. The step leaves the assigned queue once answered, so the card exits
  // and the refetch simply no longer carries it — no calendar complete/dismiss (it has no calendar item).
  const decide = (task, decision) =>
    removeWithExit(
      task,
      () => submitReviewDecision(task.actionId, task.stepId, decision),
      "Couldn't record your decision. Please try again.");

  return {
    tasks,
    outstanding,
    criticalCount,
    outstandingAmount: data?.outstandingAmount ?? 0,
    exiting,
    isLoading,
    error,
    dismiss,
    complete,
    markDone,
    decide,
  };
};
